import { List } from "immutable";

import { composeSelector } from "seneca-common/utils/selectors/compose-selectors";
import {
  getExisting as getExistingSections,
  getMany as getSections,
  listExisting as listExistingSections,
  listMany as listSections
} from "seneca-common/utils/selectors/map-query";

import { ContentId } from "../../../models/state/contents/content/Content";
import { CourseId } from "../../../models/state/courses/course/Course";
import { ModuleId } from "../../../models/state/modules/Modules";
import {
  Section,
  SectionId,
  Sections
} from "../../../models/state/sections/Sections";
import * as sectionSelectors from "./section";

export function getSection(sections: Sections, sectionId: SectionId) {
  return sections.get(sectionId);
}

export { getExistingSections, getSections, listExistingSections, listSections };

// @ts-ignore
export const getSectionId: (
  sections: Sections,
  sectionId: SectionId
) => SectionId = composeSectionSelector(sectionSelectors.getId);

// @ts-ignore
export const getSectionParentId: (
  sections: Sections,
  sectionId: SectionId
) => SectionId | CourseId = composeSectionSelector(
  sectionSelectors.getParentId
);

// @ts-ignore
export const getSectionTitle: (
  sections: Sections,
  sectionId: SectionId
) => string = composeSectionSelector(sectionSelectors.getTitle);

export function getSectionsTitles(
  sections: Sections,
  sectionIds: List<SectionId>
): List<{ header: string | null | undefined; key: string | null | undefined }> {
  return listSections(sections, sectionIds).map(section => ({
    header: section ? sectionSelectors.getTitle(section) : null,
    key: section ? sectionSelectors.getId(section) : null
  }));
}

export const getSectionSectionIds: (
  sections: Sections,
  sectionId: SectionId
) => List<SectionId> = composeSectionSelector(sectionSelectors.getSectionIds);

export const getSectionContentIds: (
  sections: Sections,
  sectionId: SectionId
) => List<ContentId> = composeSectionSelector(sectionSelectors.getContentIds);

export const getSectionExerciseContentIds: (
  sections: Sections,
  sectionId: SectionId
) => List<ContentId> = composeSectionSelector(
  sectionSelectors.getExerciseContentIds
);

export const getSectionModuleIds: (
  sections: Sections,
  sectionId: SectionId
) => List<ModuleId> = composeSectionSelector(sectionSelectors.getModuleIds);

export const isLeafSection: (
  sections: Sections,
  sectionId: SectionId
) => boolean = composeSectionSelector(sectionSelectors.isLeafSection);

export const getSectionExamQuestionsCount = composeSectionSelector(
  sectionSelectors.getSectionExamQuestionsCount
);
export const doesSectionHaveExamQuestions = composeSectionSelector(
  sectionSelectors.doesSectionHaveExamQuestions
);

function composeSectionSelector<T>(
  selector: (section: Section) => T
): (sections: Sections, sectionId: string) => T {
  // @ts-ignore
  return composeSelector(getSection, selector);
}

export function getAllChildrenSectionIds(
  sections: Sections,
  sectionId: SectionId
): List<SectionId> {
  return getSectionSectionIds(sections, sectionId).reduce(
    (sectionIds, subsectionId) => {
      return sectionIds
        .push(subsectionId)
        .concat(getAllChildrenSectionIds(sections, subsectionId));
    },
    List()
  );
}

export function recursivelyGetLeafSectionIds(
  sections: Sections,
  sectionId: SectionId
): List<string> {
  const subsectionIds = getSectionSectionIds(sections, sectionId);

  if (subsectionIds.size === 0) {
    return List([sectionId]);
  } else {
    return subsectionIds
      .map((subsectionId: string) =>
        recursivelyGetLeafSectionIds(sections, subsectionId)
      )
      .flatten() as List<string>;
  }
}

export function getAllSectionContentIds(
  sections: Sections,
  sectionId: string
): List<ContentId> {
  const immediateContentIds = getSectionContentIds(sections, sectionId);
  const allContentIds = getSectionSectionIds(sections, sectionId).reduce(
    (allContentIds, sectionId) => {
      return allContentIds.concat(getAllSectionContentIds(sections, sectionId));
    },
    immediateContentIds
  );
  return allContentIds;
}

export function getAllSectionExerciseContentIds(
  sections: Sections,
  sectionId: string
): List<ContentId> {
  const immediateContentIds = getSectionExerciseContentIds(sections, sectionId);
  const allContentIds = getSectionSectionIds(sections, sectionId).reduce(
    (allContentIds, sectionId) => {
      return allContentIds.concat(
        getAllSectionExerciseContentIds(sections, sectionId)
      );
    },
    immediateContentIds
  );
  return allContentIds;
}

export function getAllSectionContentIdsFromSections(
  sections: Sections,
  sectionIds: List<SectionId>
) {
  const contentIds = sectionIds.reduce(
    (contentIds, sectionId) =>
      contentIds.concat(getAllSectionContentIds(sections, sectionId)),
    List<string>()
  );
  return contentIds;
}

export function getAllSectionExerciseContentIdsFromSections(
  sections: Sections,
  sectionIds: List<SectionId>
) {
  const contentIds = sectionIds.reduce(
    (contentIds, sectionId) =>
      contentIds.concat(getAllSectionExerciseContentIds(sections, sectionId)),
    List<string>()
  );
  return contentIds;
}

export function getTopLevelSectionExamQuestionsCount(
  sections: Sections,
  sectionId: string
) {
  const sectionIds = getAllChildrenSectionIds(sections, sectionId);

  return sectionIds.reduce((topLevelSectionEQsCount, sectionId) => {
    const sectionEQsCount = getSectionExamQuestionsCount(sections, sectionId);
    return topLevelSectionEQsCount + (sectionEQsCount ?? 0);
  }, 0);
}
